import { useMemo, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MeetingSheet from "@/components/Agenda/MeetingSheet";
import {
  STATUS_REUNIAO,
  TIPO_LOCAL,
  duracaoEmMinutos,
  hhmm,
  horarioComFuso,
  rotuloDeDuracao,
} from "@/components/Agenda/agenda-utils";
import { CalendarClock, ExternalLink, Plus, Users } from "lucide-react";

/**
 * Reuniões de um contato ou de uma negociação, com o botão de marcar ali mesmo.
 *
 * Vive no card do contato e na oportunidade porque é onde a decisão acontece:
 * quem acabou de ler a conversa é quem sabe que a reunião precisa existir, e
 * mandá-lo para outra tela para marcar é como esse tipo de compromisso acaba
 * indo parar num calendário fora do CRM.
 */
export default function MeetingsCard({
  contactId,
  contactName,
  dealId,
  titulo = "Reuniões",
}: {
  contactId?: number | null;
  contactName?: string | null;
  dealId?: number | null;
  titulo?: string;
}) {
  const { can } = usePermissions();
  const [aberto, setAberto] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);

  const podeVer = can("agenda:view");
  const podeMarcar = can("agenda:manage") || can("agenda:manage_all");

  // Janela larga o suficiente para o histórico da negociação caber sem virar
  // uma listagem sem fim: um ano para trás, dois para a frente.
  //
  // Ancorada na MEIA-NOITE de hoje e memoizada. `new Date()` cru mudava de
  // milissegundo a cada render, e como as datas entram na chave da query o
  // react-query invalidava e refazia o pedido sem parar — um laço de
  // requisições enquanto a aba ficasse aberta. Truncar no dia também deixa a
  // resposta cacheável entre os dois cards que usam este componente.
  const { from, to } = useMemo(() => {
    const inicio = new Date();
    inicio.setHours(0, 0, 0, 0);
    const fim = new Date(inicio);
    inicio.setFullYear(inicio.getFullYear() - 1);
    fim.setFullYear(fim.getFullYear() + 2);
    return { from: inicio, to: fim };
    // Recalcula só na virada do dia — que na prática significa nunca, dentro
    // de uma sessão.
  }, [new Date().toDateString()]);

  const query = trpc.agenda.meetings.list.useQuery(
    {
      from,
      to,
      contactId: dealId ? undefined : contactId ?? undefined,
      dealId: dealId ?? undefined,
      includeCanceled: true,
    },
    { enabled: podeVer && (contactId != null || dealId != null) },
  );

  if (!podeVer) return null;

  const reunioes = query.data ?? [];
  const agora = Date.now();
  const futuras = reunioes.filter((r: any) => new Date(r.meeting.endAt).getTime() >= agora);
  const passadas = reunioes.filter((r: any) => new Date(r.meeting.endAt).getTime() < agora);

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4" />
          {titulo}
          {reunioes.length > 0 && <Badge variant="secondary">{reunioes.length}</Badge>}
        </CardTitle>
        {podeMarcar && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditandoId(null);
              setAberto(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline">Marcar reunião</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {!query.isLoading && reunioes.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nenhuma reunião registrada.
          </p>
        )}

        {futuras.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Próximas
            </p>
            {futuras.map((r: any) => (
              <LinhaDeReuniao
                key={r.meeting.id}
                row={r}
                onClick={podeMarcar ? () => { setEditandoId(r.meeting.id); setAberto(true); } : undefined}
              />
            ))}
          </div>
        )}

        {passadas.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Anteriores
            </p>
            {passadas.map((r: any) => (
              <LinhaDeReuniao
                key={r.meeting.id}
                row={r}
                onClick={podeMarcar ? () => { setEditandoId(r.meeting.id); setAberto(true); } : undefined}
              />
            ))}
          </div>
        )}
      </CardContent>

      <MeetingSheet
        open={aberto}
        onClose={() => setAberto(false)}
        meetingId={editandoId}
        defaults={{ contactId, contactName, dealId }}
      />
    </Card>
  );
}

function LinhaDeReuniao({ row, onClick }: { row: any; onClick?: () => void }) {
  const m = row.meeting;
  const conteudo = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{m.title}</span>
        <Badge className={`${STATUS_REUNIAO[m.status]?.tint} text-[11px]`}>
          {STATUS_REUNIAO[m.status]?.label ?? m.status}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">
        {new Date(m.startAt).toLocaleDateString("pt-BR", {
          weekday: "short",
          day: "2-digit",
          month: "short",
        })}
        {" · "}
        {hhmm(m.startAt)}
        {" · "}
        {rotuloDeDuracao(duracaoEmMinutos(m.startAt, m.endAt))}
        {" · "}
        {TIPO_LOCAL[m.locationType]}
      </p>
      {/* Só aparece quando a reunião foi marcada num fuso em que o relógio
          marca outra hora — para quem nunca sai do próprio, seria ruído. */}
      {(() => {
        const h = horarioComFuso(m.startAt, m.endAt, m.timezone);
        return h.local ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            {h.principal} em {h.rotulo}
          </p>
        ) : null;
      })()}
      {(row.attendees ?? []).length > 0 && (
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          <Users className="h-3 w-3" />
          {row.attendees.map((a: any) => a.user?.name).filter(Boolean).join(", ")}
        </p>
      )}
      {m.briefing && (
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.briefing}</p>
      )}
    </>
  );

  return (
    <div
      className={`rounded-md border border-border border-l-4 px-3 py-2 ${
        STATUS_REUNIAO[m.status]?.barra ?? "border-l-slate-400"
      }`}
    >
      {onClick ? (
        <button type="button" onClick={onClick} className="w-full text-left">
          {conteudo}
        </button>
      ) : (
        conteudo
      )}
      {m.meetingUrl && (
        <a
          href={m.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1"
        >
          Entrar na reunião <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}
